import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Card, CardBody, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Spinner } from 'reactstrap';
import { get, post } from 'helpers/api_helper';
import Datatables from 'pages/utils/table/datatable';
import { BILLING_URL, PAYMENT_URL, PATIENT_URL, DOCTOR_URL, TREATMENT_URL } from 'helpers/url_helper';
import { showSuccessAlert, showErroeAlert } from "pages/utils/alertMessages";
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import cookieHelper from 'helpers/getCookieData';
import { openInvoicePrintWindow } from 'pages/utils/printHelpers';
import {
  RiBillLine,
  RiPrinterLine,
  RiHandCoinLine,
  RiCalendarEventLine,
  RiUserHeartLine,
  RiCheckDoubleLine,
  RiCloseLine,
  RiDownload2Line,
  RiStethoscopeLine,
  RiShieldCheckLine,
  RiMoneyDollarCircleLine,
  RiBankCardLine,
  RiQrCodeLine,
  RiWallet3Line,
  RiCheckLine
} from 'react-icons/ri';

const ManageBill = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState({ totalItems: 0, items: [] });
  const [loading, setLoading] = useState(false);
  const [selectedBill, setSelectedBill] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);

  // Payment Modal State
  const [selectedPaymentBill, setSelectedPaymentBill] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [submittingPayment, setSubmittingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'Cash',
    date: moment().format('YYYY-MM-DD'),
    remark: ''
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    Search: '',
  });

  const clinic = useMemo(() => {
    try {
      const raw = cookieHelper.getCookie('_c');
      return raw ? JSON.parse(atob(raw)) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const fetchBills = async () => {
    setLoading(true);
    try {
      const res = await get(
        `${BILLING_URL}?page=${pagination.page}&limit=${pagination.limit}&search=${pagination.Search || ''}`
      );
      if (res?.success || res?.items || Array.isArray(res?.body?.items)) {
        const items = res?.items || res?.body?.items || (Array.isArray(res) ? res : []);
        const total = res?.totalItems || res?.body?.totalItems || items.length;
        setRows({ totalItems: total, items });
      } else {
        setRows({ totalItems: 0, items: [] });
      }
    } catch (e) {
      console.error("Error fetching bills:", e);
      setRows({ totalItems: 0, items: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBills();
  }, [pagination]);

  const handlePrintInvoice = (bill) => {
    openInvoicePrintWindow(bill, clinic);
  };

  const handleTriggerPrint = () => {
    if (selectedBill) {
      openInvoicePrintWindow(selectedBill, clinic);
    } else {
      window.print();
    }
  };

  const handleOpenPaymentModal = (bill) => {
    setSelectedPaymentBill(bill);
    const pendingAmt = parseFloat(bill.pending || 0);
    const billNum = bill.billing_no || `INV-${bill.id}`;
    setPaymentForm({
      amount: pendingAmt > 0 ? pendingAmt : '',
      payment_mode: 'Cash',
      date: moment().format('YYYY-MM-DD'),
      remark: `Payment settlement for Invoice #${billNum}`
    });
    setIsPaymentModalOpen(true);
  };

  const handleSavePayment = async (e) => {
    if (e) e.preventDefault();
    if (!selectedPaymentBill) return;

    const amt = parseFloat(paymentForm.amount);
    const pendingAmt = parseFloat(selectedPaymentBill.pending || 0);

    if (!amt || amt <= 0) {
      showErroeAlert(t("Please enter a valid payment amount greater than zero."));
      return;
    }

    if (pendingAmt > 0 && amt > pendingAmt) {
      showErroeAlert(t(`Payment amount cannot exceed the remaining balance due of ₹${pendingAmt.toLocaleString('en-IN')}.`));
      return;
    }

    setSubmittingPayment(true);
    try {
      const patientId = selectedPaymentBill.patient_id || selectedPaymentBill.patients?.id || selectedPaymentBill.patient?.id;
      const payload = {
        patient_id: patientId,
        clinic_id: selectedPaymentBill.clinic_id || clinic?.id || 8,
        client_id: selectedPaymentBill.client_id || clinic?.client_id || 71,
        billing_no: selectedPaymentBill.billing_no,
        amount: amt,
        payment_mode: paymentForm.payment_mode || 'Cash',
        remark: paymentForm.remark || `Payment on Invoice #${selectedPaymentBill.billing_no || selectedPaymentBill.id}`,
        date: paymentForm.date ? moment(paymentForm.date).format("YYYY-MM-DD HH:mm:ss") : moment().format("YYYY-MM-DD HH:mm:ss")
      };

      const res = await post(PAYMENT_URL, payload);
      if (res?.success || res?.id || res?.body) {
        showSuccessAlert(t("Payment recorded successfully!"));
        setIsPaymentModalOpen(false);
        setSelectedPaymentBill(null);
        fetchBills();
      } else {
        showErroeAlert(res?.message || res?.error || "Failed to record payment.");
      }
    } catch (err) {
      console.error("Error recording payment:", err);
      showErroeAlert(err?.response?.data?.message || err.message || "Error recording payment.");
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getPaymentStatusBadge = (row) => {
    const total = parseFloat(row.total || 0);
    const paid = parseFloat(row.paid || 0);
    const pending = parseFloat(row.pending || 0);

    if (row.status === 3) {
      return (
        <span className="badge bg-light text-muted border px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold">
          Cancelled
        </span>
      );
    }

    if (paid >= total && total > 0) {
      return (
        <span className="badge bg-success-subtle text-success border border-success-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
          ✓ {t("Fully Paid")}
        </span>
      );
    }

    if (paid > 0 && pending > 0) {
      return (
        <span className="badge bg-warning-subtle text-warning border border-warning-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
          ● {t("Partially Paid")}
        </span>
      );
    }

    return (
      <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold d-inline-flex align-items-center gap-1">
        ● {t("Unpaid")}
      </span>
    );
  };

  const billColumns = [
    {
      dataField: 'id',
      text: '#',
      style: { width: '45px', textAlign: 'center', verticalAlign: 'middle' },
      formatter: (cell, row, rowIndex) => (
        <span className="badge bg-light text-muted border font-size-11 font-weight-bold">
          {(pagination?.page - 1) * (pagination?.limit || 10) + (rowIndex + 1)}
        </span>
      )
    },
    {
      dataField: 'billing_no',
      text: 'Invoice #',
      style: { width: '130px', verticalAlign: 'middle' },
      formatter: (cell, row) => (
        <div>
          <span className="badge bg-primary-subtle text-primary border border-primary-subtle font-monospace font-weight-bold font-size-11 px-2 py-1">
            {cell || `INV-${row.id}`}
          </span>
          <div className="font-size-11 text-muted mt-0.5 d-flex align-items-center gap-1">
            <RiCalendarEventLine className="text-muted" />
            {row.date ? moment(row.date).format('DD MMM YYYY') : moment(row.created_at).format('DD MMM YYYY')}
          </div>
        </div>
      )
    },
    {
      dataField: 'patients',
      text: 'Patient Details',
      style: { verticalAlign: 'middle' },
      formatter: (cell, row) => {
        const patient = row.patients || row.patient || {};
        const fullName = `${patient.title || ''} ${patient.first_name || ''} ${patient.last_name || ''}`.trim() ||
                         patient.full_name || row.patient_name || 'Patient';
        const mobile = patient.mobile || row.patient_mobile;
        const caseNo = patient.case_no;
        const initials = fullName.split(' ').filter(Boolean).map(n => n[0]).join('').substring(0, 2).toUpperCase() || 'P';

        return (
          <div className="d-flex align-items-center gap-2.5 py-1">
            <div
              className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center font-weight-bold font-size-12 flex-shrink-0 shadow-2xs"
              style={{ width: '34px', height: '34px' }}
            >
              {initials}
            </div>
            <div>
              <div className="font-weight-bold text-dark font-size-13">
                {fullName}
              </div>
              <div className="d-flex align-items-center gap-1.5 mt-0.5 flex-wrap">
                {caseNo && (
                  <span className="badge bg-light text-dark border font-size-10 px-1.5 py-0.5">
                    #{caseNo}
                  </span>
                )}
                {mobile && (
                  <span className="text-muted font-size-11">📞 {mobile}</span>
                )}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      dataField: 'treatment_info',
      text: 'Procedures / Description',
      style: { verticalAlign: 'middle' },
      formatter: (cell, row) => {
        let desc = "Dental Consultation & Treatment";
        if (cell) {
          try {
            const parsed = typeof cell === 'string' ? JSON.parse(cell) : cell;
            if (Array.isArray(parsed) && parsed.length > 0) {
              desc = parsed.map(item => {
                const type = item._type || item.type || item.title || item.name || 'Procedure';
                const tooth = item.tooth || item.teeth ? ` (Tooth #${item.tooth || item.teeth})` : '';
                return `${type}${tooth}`;
              }).join(', ');
            } else if (parsed && typeof parsed === 'object') {
              desc = parsed._type || parsed.title || parsed.name || "Dental Service";
            } else {
              desc = String(cell);
            }
          } catch (e) {
            desc = String(cell);
          }
        } else if (row.treatment?.title) {
          desc = row.treatment.title;
        }

        return (
          <div className="d-flex align-items-center gap-1.5 py-1">
            <div className="bg-primary-subtle text-primary p-1 rounded-circle d-flex align-items-center justify-content-center font-size-11 flex-shrink-0">
              <i className="fas fa-tooth" />
            </div>
            <span className="font-weight-semibold text-dark font-size-12 text-truncate" style={{ maxWidth: '240px' }} title={desc}>
              {desc}
            </span>
          </div>
        );
      }
    },
    {
      dataField: 'total',
      text: 'Total Billed',
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerStyle: { textAlign: 'right' },
      formatter: (cell) => (
        <span className="font-weight-bold text-dark font-size-13">
          ₹{parseFloat(cell || 0).toLocaleString('en-IN')}
        </span>
      )
    },
    {
      dataField: 'paid',
      text: 'Paid Amount',
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerStyle: { textAlign: 'right' },
      formatter: (cell) => (
        <span className="font-weight-bold text-success font-size-13">
          ₹{parseFloat(cell || 0).toLocaleString('en-IN')}
        </span>
      )
    },
    {
      dataField: 'pending',
      text: 'Balance Due',
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerStyle: { textAlign: 'right' },
      formatter: (cell, row) => {
        const due = parseFloat(cell || 0);
        return due > 0 ? (
          <span className="badge bg-danger-subtle text-danger border border-danger-subtle font-weight-bold font-size-11 px-2 py-1">
            ₹{due.toLocaleString('en-IN')}
          </span>
        ) : (
          <span className="text-muted font-size-12">₹0</span>
        );
      }
    },
    {
      dataField: 'status',
      text: 'Status',
      style: { verticalAlign: 'middle', textAlign: 'center' },
      headerStyle: { textAlign: 'center' },
      formatter: (cell, row) => getPaymentStatusBadge(row)
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      style: { verticalAlign: 'middle', textAlign: 'center', minWidth: '175px' },
      headerStyle: { textAlign: 'center', minWidth: '175px' },
      formatter: (cell, row) => {
        const isPending = row.status !== 3 && parseFloat(row.pending || 0) > 0;
        return (
          <div className="d-flex align-items-center justify-content-center gap-1.5 flex-wrap">
            {isPending && (
              <button
                type="button"
                className="btn btn-sm btn-success d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold shadow-2xs"
                onClick={() => handleOpenPaymentModal(row)}
                title={t("Record & Add Payment")}
              >
                <RiHandCoinLine className="font-size-13" /> {t("Add Payment")}
              </button>
            )}
            <button
              type="button"
              className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 px-2.5 py-1 rounded-pill font-size-11 font-weight-semibold shadow-2xs"
              onClick={() => handlePrintInvoice(row)}
              title={t("View & Print Official Invoice")}
            >
              <RiPrinterLine className="font-size-13" /> {t("Print")}
            </button>
          </div>
        );
      }
    }
  ];

  return (
    <React.Fragment>
      <Datatables
        isSearch={true}
        columns={billColumns}
        showTableOnly={true}
        rowsLength={rows?.totalItems || 0}
        rows={rows?.items || []}
        keyField={'id'}
        title={t("All Clinical Invoices & Bills")}
        isAdd={false}
        isTableHead={true}
        loading={loading}
        ssr={setPagination}
      />

      {/* Record Payment Modal */}
      <Modal isOpen={isPaymentModalOpen} toggle={() => !submittingPayment && setIsPaymentModalOpen(false)} size="md" centered>
        <ModalHeader toggle={() => !submittingPayment && setIsPaymentModalOpen(false)} className="border-bottom bg-light py-2.5 px-3">
          <div className="d-flex align-items-center gap-2">
            <div className="bg-success-subtle text-success p-1.5 rounded-circle d-flex align-items-center justify-content-center font-size-16">
              <RiHandCoinLine />
            </div>
            <div>
              <span className="font-weight-bold font-size-15 text-dark">{t("Record Payment")}</span>
              <div className="font-size-11 text-muted font-monospace">
                {selectedPaymentBill?.billing_no || `INV-${selectedPaymentBill?.id}`}
              </div>
            </div>
          </div>
        </ModalHeader>
        <Form onSubmit={handleSavePayment}>
          <ModalBody className="p-3">
            {selectedPaymentBill && (
              <>
                {/* Patient & Invoice Summary Card */}
                <div className="bg-light rounded-3 p-3 mb-3 border">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="text-muted font-size-11">{t("Patient Name")}</div>
                      <div className="font-weight-bold text-dark font-size-13">
                        {selectedPaymentBill.patients?.full_name ||
                         `${selectedPaymentBill.patients?.first_name || ''} ${selectedPaymentBill.patients?.last_name || ''}`.trim() ||
                         selectedPaymentBill.patient_name || 'Patient'}
                      </div>
                      {selectedPaymentBill.patients?.mobile && (
                        <div className="text-muted font-size-11">
                          📞 {selectedPaymentBill.patients.mobile}
                        </div>
                      )}
                    </div>
                    <div className="text-end">
                      <div className="text-muted font-size-11">{t("Invoice Status")}</div>
                      <div className="mt-0.5">
                        {getPaymentStatusBadge(selectedPaymentBill)}
                      </div>
                    </div>
                  </div>

                  {/* Financial Stats Bar */}
                  <div className="row g-2 pt-2 border-top text-center font-size-12">
                    <div className="col-4">
                      <div className="text-muted font-size-10">{t("Total Billed")}</div>
                      <div className="font-weight-bold text-dark">
                        ₹{parseFloat(selectedPaymentBill.total || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="col-4 border-start border-end">
                      <div className="text-muted font-size-10">{t("Paid so far")}</div>
                      <div className="font-weight-bold text-success">
                        ₹{parseFloat(selectedPaymentBill.paid || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="text-muted font-size-10">{t("Balance Due")}</div>
                      <div className="font-weight-bold text-danger">
                        ₹{parseFloat(selectedPaymentBill.pending || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form Fields */}
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Label className="form-label font-size-12 font-weight-semibold text-dark mb-0">
                      {t("Payment Amount (₹)")} <span className="text-danger">*</span>
                    </Label>
                    {parseFloat(selectedPaymentBill.pending || 0) > 0 && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 font-size-11 text-decoration-none text-primary font-weight-semibold"
                        onClick={() => setPaymentForm(prev => ({ ...prev, amount: parseFloat(selectedPaymentBill.pending || 0) }))}
                      >
                        {t("Pay Full Due (₹")}{parseFloat(selectedPaymentBill.pending || 0).toLocaleString('en-IN')})
                      </button>
                    )}
                  </div>
                  <div className="input-group">
                    <span className="input-group-text bg-light text-dark font-weight-bold">₹</span>
                    <Input
                      type="number"
                      step="any"
                      min="1"
                      max={parseFloat(selectedPaymentBill.pending || 0) || undefined}
                      className="form-control font-weight-bold font-size-14"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                      placeholder="0.00"
                      required
                      autoFocus
                    />
                  </div>
                </div>

                <div className="row g-2 mb-3">
                  <div className="col-sm-6">
                    <Label className="form-label font-size-12 font-weight-semibold text-dark mb-1">
                      {t("Payment Mode")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      className="form-select form-control font-size-12"
                      value={paymentForm.payment_mode}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_mode: e.target.value })}
                      required
                    >
                      <option value="Cash">💵 Cash</option>
                      <option value="UPI / QR Code">📱 UPI / QR Code (GPay / PhonePe / Paytm)</option>
                      <option value="Debit Card">💳 Debit Card (POS)</option>
                      <option value="Credit Card">💳 Credit Card (POS)</option>
                      <option value="Bank Transfer">🏦 Bank Transfer / NEFT / IMPS</option>
                      <option value="Cheque">📝 Cheque</option>
                    </Input>
                  </div>
                  <div className="col-sm-6">
                    <Label className="form-label font-size-12 font-weight-semibold text-dark mb-1">
                      {t("Payment Date")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="date"
                      className="form-control font-size-12"
                      value={paymentForm.date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="mb-2">
                  <Label className="form-label font-size-12 font-weight-semibold text-dark mb-1">
                    {t("Remarks / Reference Details")}
                  </Label>
                  <Input
                    type="text"
                    className="form-control font-size-12"
                    placeholder="e.g. UPI Ref #, Cheque #, or transaction note..."
                    value={paymentForm.remark}
                    onChange={(e) => setPaymentForm({ ...paymentForm, remark: e.target.value })}
                  />
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter className="bg-light py-2 px-3 border-top">
            <Button
              type="button"
              color="light"
              className="border px-3"
              disabled={submittingPayment}
              onClick={() => setIsPaymentModalOpen(false)}
            >
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              color="success"
              className="d-inline-flex align-items-center gap-1.5 px-3 font-weight-semibold shadow-2xs"
              disabled={submittingPayment}
            >
              {submittingPayment ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {t("Processing...")}
                </>
              ) : (
                <>
                  <RiCheckLine className="font-size-16" />
                  {t("Confirm Payment")} {paymentForm.amount ? `(₹${parseFloat(paymentForm.amount || 0).toLocaleString('en-IN')})` : ''}
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Official Dental Invoice Modal */}
      <Modal isOpen={isInvoiceModalOpen} toggle={() => setIsInvoiceModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setIsInvoiceModalOpen(false)} className="border-bottom-0 pb-0">
          <span className="font-weight-bold font-size-16">{t("Tax Invoice & Billing Receipt")}</span>
        </ModalHeader>
        <ModalBody className="p-4">
          {selectedBill && (
            <div id="printable-invoice-content">
              {/* Header Box */}
              <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                <div>
                  <h4 className="font-weight-bold text-primary mb-1">{clinic?.clinic_name || "909 Dental OPD & Implant Clinic"}</h4>
                  <p className="text-muted font-size-12 mb-0" style={{ maxWidth: '320px' }}>
                    {clinic?.address || "Modern Dental Clinic, Oral Health & Implantology Center"}<br />
                    Phone: {clinic?.phone || "098220 11111"} • Email: {clinic?.email || "billing@oralstop.com"}
                  </p>
                </div>
                <div className="text-end">
                  <span className="badge bg-primary px-2.5 py-1 font-size-12 mb-1">OFFICIAL INVOICE</span>
                  <div className="font-weight-bold font-monospace text-dark font-size-14">
                    #{selectedBill.billing_no || `INV-${selectedBill.id}`}
                  </div>
                  <div className="text-muted font-size-11">
                    Date: {selectedBill.date ? moment(selectedBill.date).format('DD MMM YYYY') : moment().format('DD MMM YYYY')}
                  </div>
                </div>
              </div>

              {/* Patient Meta */}
              <div className="bg-light p-3 rounded-3 mb-3">
                <Row className="g-2 font-size-12">
                  <Col sm={6}>
                    <span className="text-muted">Billed To (Patient):</span>
                    <div className="font-weight-bold text-dark font-size-13 mt-0.5">
                      {selectedBill.patients?.full_name || selectedBill.patient?.full_name || selectedBill.patient_name || "Patient"}
                    </div>
                    <span className="text-muted">
                      Case ID: #{selectedBill.patients?.case_no || "N/A"} • Phone: {selectedBill.patients?.mobile || "N/A"}
                    </span>
                  </Col>
                  <Col sm={6} className="text-sm-end">
                    <span className="text-muted">Payment Status:</span>
                    <div className="mt-0.5">
                      {getPaymentStatusBadge(selectedBill)}
                    </div>
                    <span className="text-muted font-size-11">
                      Mode: {selectedBill.payment_mode || "Cash / Online UPI"}
                    </span>
                  </Col>
                </Row>
              </div>

              {/* Itemized Table */}
              <table className="table table-bordered align-middle font-size-12 mb-3">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Procedure / Particulars</th>
                    <th className="text-end">Standard Fee</th>
                    <th className="text-end">Discount</th>
                    <th className="text-end">Net Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-center">1</td>
                    <td>
                      <strong>{selectedBill.treatment_info || selectedBill.treatment?.title || "Dental Consultation & Treatment"}</strong>
                    </td>
                    <td className="text-end">₹{parseFloat(selectedBill.total || 0).toLocaleString('en-IN')}</td>
                    <td className="text-end">₹{parseFloat(selectedBill.discount || 0).toLocaleString('en-IN')}</td>
                    <td className="text-end font-weight-bold">₹{parseFloat(selectedBill.total || 0).toLocaleString('en-IN')}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="text-end font-weight-bold">Total Invoiced:</td>
                    <td className="text-end font-weight-bold text-dark font-size-13">
                      ₹{parseFloat(selectedBill.total || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="text-end font-weight-bold text-success">Amount Received:</td>
                    <td className="text-end font-weight-bold text-success font-size-13">
                      ₹{parseFloat(selectedBill.paid || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="4" className="text-end font-weight-bold text-danger">Outstanding Balance Due:</td>
                    <td className="text-end font-weight-bold text-danger font-size-13">
                      ₹{parseFloat(selectedBill.pending || 0).toLocaleString('en-IN')}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {/* Signature stamp */}
              <div className="d-flex justify-content-between align-items-end pt-3 border-top mt-4 font-size-11 text-muted">
                <div>
                  <RiShieldCheckLine className="text-success me-1" /> Computer-generated official dental tax invoice.
                </div>
                <div className="text-center">
                  <div style={{ height: '35px' }}></div>
                  <div className="border-top pt-1 font-weight-bold text-dark" style={{ minWidth: '140px' }}>
                    Authorized Signatory
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
            <Button color="light" onClick={() => setIsInvoiceModalOpen(false)}>
              {t("Close")}
            </Button>
            <Button color="primary" onClick={handleTriggerPrint}>
              <RiPrinterLine className="me-1" /> {t("Print Invoice")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ManageBill;
